import * as React from 'react'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Pill } from '@/components/primitives/Pill'
import { Spinner } from '@/components/feedback/Spinner'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { listFaxJobs, retrySingle, retryBulk, type FaxJob, type StatusFilter } from './api'
import { listResidents } from '@/features/residents/api'
import { useAuth } from '@/features/auth/AuthContext'
import { useFacilityFilter } from '@/store/facilityFilterStore'
import './FailedQueueDrawer.css'

interface Props {
  open: boolean
  onClose: () => void
}

const TABS: { label: string; value: StatusFilter }[] = [
  { label: 'All', value: 'all' },
  { label: 'Failed', value: 'failed' },
  { label: 'Sent', value: 'sent' },
]

const STATUS_VARIANT: Record<string, 'success' | 'danger' | 'warning' | 'neutral'> = {
  sent: 'success',
  failed: 'danger',
  permanently_failed: 'danger',
  queued: 'neutral',
  sending: 'warning',
  canceled: 'neutral',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function friendlyError(raw: string | null): string | null {
  if (!raw) return null
  if (raw.includes('Senders Email')) return 'Sender email not configured'
  if (raw.includes('CallerID')) return 'Invalid caller ID'
  if (raw.includes('Cover Page')) return 'Invalid cover page setting'
  if (raw.includes('11 digits') || raw.includes('Fax Number')) return 'Invalid fax number format'
  if (raw.includes('SRFax error')) return 'Fax delivery error'
  return raw.length > 55 ? raw.slice(0, 52) + '…' : raw
}

export function FailedQueueDrawer({ open, onClose }: Props): React.JSX.Element {
  const qc = useQueryClient()
  const toast = useToast()
  const { user } = useAuth()
  const { facilityId: filterFacilityId } = useFacilityFilter()
  const activeFacilityId = user?.role === 'SUPER_ADMIN' ? filterFacilityId : (user?.facility_id ?? null)
  const [tab, setTab] = useState<StatusFilter>('all')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data: allJobs = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ['fax-jobs', activeFacilityId],
    queryFn: () => listFaxJobs(undefined, activeFacilityId),
    enabled: open,
    refetchInterval: open ? 15_000 : false,
    placeholderData: keepPreviousData,
  })

  const { data: allResidents = [] } = useQuery({
    queryKey: ['residents', 'all-statuses'],
    queryFn: async () => {
      const [active, discharged, transferred, deceased] = await Promise.all([
        listResidents({ status: 'active', page_size: 200, page: 0 }),
        listResidents({ status: 'discharged', page_size: 200, page: 0 }),
        listResidents({ status: 'transferred', page_size: 200, page: 0 }),
        listResidents({ status: 'deceased', page_size: 200, page: 0 }),
      ])
      return [...active, ...discharged, ...transferred, ...deceased]
    },
    enabled: open,
    staleTime: 60_000,
  })
  const residentMap = new Map(allResidents.map(r => [r.id, `${r.first_name} ${r.last_name}`]))

  useEffect(() => { setSelected(new Set()) }, [tab])

  const jobs: FaxJob[] = tab === 'all'
    ? allJobs
    : tab === 'queued'
      ? allJobs.filter(j => j.status === 'queued' || j.status === 'sending')
      : allJobs.filter(j => j.status === tab)

  const failedJobs = allJobs.filter((j: FaxJob) => j.status === 'failed' || j.status === 'permanently_failed')
  const failedIds = failedJobs.map((j: FaxJob) => j.id)
  const selectedFailed = [...selected].filter(id => failedIds.includes(id))
  const allFailedSelected = failedIds.length > 0 && failedIds.every((id: string) => selected.has(id))

  const retrySingleMut = useMutation({
    mutationFn: (id: string) => retrySingle(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['fax-jobs'] })
      toast.success('Retrying', 'Job has been re-queued.')
    },
    onError: (err) => toast.error('Retry failed', err instanceof ApiError ? err.message : 'Try again.'),
  })

  const retryBulkMut = useMutation({
    mutationFn: (ids?: string[]) => retryBulk(ids),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['fax-jobs'] })
      setSelected(new Set())
      toast.success('Retrying', `${res.retried} job${res.retried !== 1 ? 's' : ''} re-queued.`)
    },
    onError: (err) => toast.error('Retry failed', err instanceof ApiError ? err.message : 'Try again.'),
  })

  function toggleSelect(id: string): void {
    setSelected(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  function toggleAllFailed(): void {
    if (allFailedSelected) {
      setSelected(prev => { const n = new Set(prev); failedIds.forEach((id: string) => n.delete(id)); return n })
    } else {
      setSelected(prev => new Set([...prev, ...failedIds]))
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Queues"
      subtitle="Vital sends — all, failed & sent"
      headerAction={
        <Button
          variant="primary"
          size="sm"
          disabled={failedJobs.length === 0}
          loading={retryBulkMut.isPending}
          onClick={() => retryBulkMut.mutate(undefined)}
          leftIcon={<RetryIcon />}
        >
          Retry all{failedJobs.length > 0 ? ` (${failedJobs.length})` : ''}
        </Button>
      }
      footer={<Button variant="ghost" onClick={onClose} type="button">Close</Button>}
    >
      {/* Tabs */}
      <div className="fax-tabs">
        {TABS.map(t => {
          const count = t.value === 'all'
            ? allJobs.length
            : t.value === 'failed'
              ? failedJobs.length
              : allJobs.filter(j => j.status === t.value).length
          return (
            <button
              key={t.value}
              type="button"
              className={'fax-tab' + (tab === t.value ? ' fax-tab-active' : '')}
              onClick={() => setTab(t.value)}
            >
              {t.label}
              <span className="fax-tab-badge">{count}</span>
            </button>
          )
        })}
        <button type="button" className={'fax-refresh-btn' + (isFetching && !isLoading ? ' fax-refresh-btn--spinning' : '')} onClick={() => void refetch()} title="Refresh">
          <RefreshIcon />
        </button>
      </div>

      {/* Bulk select bar */}
      {failedIds.length > 0 && (tab === 'all' || tab === 'failed') && (
        <div className={'fax-bulk-bar' + (selectedFailed.length > 0 ? ' fax-bulk-bar--active' : '')}>
          <label className="fax-bulk-row">
            <input
              type="checkbox"
              className="fax-checkbox"
              checked={allFailedSelected}
              onChange={toggleAllFailed}
            />
            <span className="fax-bulk-label">
              {selectedFailed.length > 0
                ? `${selectedFailed.length} selected`
                : 'Select all failed'}
            </span>
          </label>
          {selectedFailed.length > 0 && (
            <Button
              variant="primary"
              size="sm"
              loading={retryBulkMut.isPending}
              onClick={() => retryBulkMut.mutate(selectedFailed)}
              leftIcon={<RetryIcon />}
            >
              Retry selected ({selectedFailed.length})
            </Button>
          )}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
          <Spinner size="lg" />
        </div>
      ) : jobs.length === 0 ? (
        <div className="fax-empty">
          <InboxIcon />
          <span>No vitals in the queue yet.</span>
        </div>
      ) : (
        <div className="fax-list">
          {jobs.map((job: FaxJob) => {
            const isFailed = job.status === 'failed' || job.status === 'permanently_failed'
            const isChecked = selected.has(job.id)
            return (
              <div key={job.id} className={'fax-row' + (isChecked ? ' fax-row-selected' : '')}>
                <div className="fax-row-check">
                  {isFailed
                    ? <input type="checkbox" className="fax-checkbox" checked={isChecked} onChange={() => toggleSelect(job.id)} />
                    : <div style={{ width: 16 }} />}
                </div>
                <div className={`fax-dot fax-dot--${job.status}`} />
                <div className="fax-row-info">
                  <div className="fax-row-top">
                    <span className="fax-name">{
                      job.resident_ids.length === 0 ? 'Residents' :
                      job.resident_ids.length === 1 ? (residentMap.get(job.resident_ids[0]!) ?? 'Unknown resident') :
                      job.resident_ids.map(id => residentMap.get(id) ?? id).join(', ')
                    }</span>
                    <span className="fax-row-arrow">→</span>
                    {job.recipient_name && <span className="fax-recipient">{job.recipient_name}</span>}
                    {job.to_fax_number && <span className="fax-number mono">{job.to_fax_number}</span>}
                  </div>
                  <div className="fax-row-sub muted small">
                    {formatDate(job.created_at)}
                    {job.attempts > 1 && <span className="fax-attempts-badge"> · {job.attempts} attempts</span>}
                    {job.last_error && <span className="fax-error-msg" title={job.last_error}> · {friendlyError(job.last_error)}</span>}
                  </div>
                </div>
                <Pill variant={STATUS_VARIANT[job.status] ?? 'neutral'}>
                  {job.status.replace('_', ' ')}
                </Pill>
                {isFailed && selected.size === 0 ? (
                  <button
                    type="button"
                    className="fax-retry-btn"
                    disabled={retrySingleMut.isPending && retrySingleMut.variables === job.id}
                    onClick={() => retrySingleMut.mutate(job.id)}
                  >
                    <RetryIcon />
                    Retry
                  </button>
                ) : <div style={{ width: 72 }} />}
              </div>
            )
          })}
        </div>
      )}
    </Drawer>
  )
}

function RetryIcon(): React.JSX.Element {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-4.95"/>
    </svg>
  )
}
function RefreshIcon(): React.JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-4.95"/>
    </svg>
  )
}
function InboxIcon(): React.JSX.Element {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
      <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
    </svg>
  )
}
